'use client';

import { type FormEvent, useCallback, useMemo, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { Building2, TriangleAlert } from 'lucide-react';

import { useSignInWithSSO } from '@kit/supabase/hooks/use-sign-in-with-sso';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import { Spinner } from '@kit/ui/spinner';
import { Trans } from '@kit/ui/trans';

import { useLastAuthMethod } from '../hooks/use-last-auth-method';
import { AuthErrorAlert } from './auth-error-alert';

/**
 * The app's server action that answers "is this email's domain SSO-enrolled?".
 * It has to run server-side: logged-out visitors cannot query `public`.
 */
export type SsoDomainLookupAction = (input: {
  email: string;
}) => Promise<{ data?: { sso: boolean } } | undefined>;

export type SsoDomainGuardConfig = {
  /** Callback URL SSO returns to, same as the magic link's. */
  redirectTo: string;
  lookup: SsoDomainLookupAction;
};

// Longer than the server's own database timeout, so this only fires when the
// action itself never answers.
const LOOKUP_TIMEOUT_MS = 8000;

async function lookupSsoDomain(lookup: SsoDomainLookupAction, email: string) {
  const domain = email.split('@')[1]?.trim().toLowerCase();

  if (!domain) {
    throw new Error('SSO domain lookup needs an email with a domain');
  }

  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(new Error('SSO domain lookup timed out')),
      LOOKUP_TIMEOUT_MS,
    );
  });

  try {
    const result = await Promise.race([lookup({ email }), timeout]);
    const sso = result?.data?.sso;

    if (typeof sso !== 'boolean') {
      throw new Error('SSO domain lookup did not return an answer');
    }

    return { sso, domain };
  } finally {
    clearTimeout(timer);
  }
}

type GuardState =
  | { status: 'idle' }
  | { status: 'sso'; domain: string }
  | { status: 'failed' };

/**
 * Stops the email-based sign-in methods (password, magic link, OTP) from
 * submitting an email whose domain is SSO-enrolled, and offers SSO instead.
 *
 * `canUseEmail` resolves `false` both for an SSO domain and when the lookup
 * fails: a failed lookup never falls back to "email is fine" (ARK-2554). The
 * `before_user_created` hook is the enforcement; this is the UX in front of it.
 *
 * Attach `onEmailInput` as `onInput` on an element wrapping the form: editing
 * the email clears a notice that was about the previous address.
 *
 * While `ssoDetected` is true, hide the form's own submit: "Continue with SSO"
 * is the only action that can move sign-in forward.
 */
export function useSsoDomainGuard({
  redirectTo,
  lookup,
}: SsoDomainGuardConfig) {
  const { mutateAsync: lookupDomain, isPending: isChecking } = useMutation({
    mutationKey: ['auth', 'sso-domain-lookup'],
    mutationFn: (email: string) => lookupSsoDomain(lookup, email),
  });
  const [state, setState] = useState<GuardState>({ status: 'idle' });

  const onEmailInput = useCallback((event: FormEvent<HTMLElement>) => {
    const target = event.target as HTMLInputElement;

    if (target.name === 'email') {
      setState({ status: 'idle' });
    }
  }, []);

  const canUseEmail = useCallback(
    async (email: string) => {
      setState({ status: 'idle' });

      try {
        const result = await lookupDomain(email);

        if (result.sso) {
          setState({ status: 'sso', domain: result.domain });
          return false;
        }

        return true;
      } catch (error) {
        console.error('SSO domain lookup failed', error);
        setState({ status: 'failed' });
        return false;
      }
    },
    [lookupDomain],
  );

  const notice = useMemo(() => {
    if (state.status === 'sso') {
      return (
        <SsoDomainDetectedNotice
          domain={state.domain}
          redirectTo={redirectTo}
        />
      );
    }

    return state.status === 'failed' ? <SsoDomainLookupFailedNotice /> : null;
  }, [state, redirectTo]);

  const ssoDetected = state.status === 'sso';

  return useMemo(
    () => ({ canUseEmail, isChecking, notice, onEmailInput, ssoDetected }),
    [canUseEmail, isChecking, notice, onEmailInput, ssoDetected],
  );
}

function SsoDomainDetectedNotice(props: {
  domain: string;
  redirectTo: string;
}) {
  const signInWithSSO = useSignInWithSSO();
  const { recordAuthMethod } = useLastAuthMethod();
  const isStarting = signInWithSSO.isPending || signInWithSSO.isSuccess;

  return (
    <div className="flex flex-col gap-y-3" data-test="sso-domain-notice">
      <Alert>
        <Building2 className="h-4" />

        <AlertTitle>
          <Trans i18nKey="auth.ssoDomainDetectedTitle" />
        </AlertTitle>

        <AlertDescription>
          <Trans i18nKey="auth.ssoDomainDetectedDescription" />
        </AlertDescription>
      </Alert>

      <Button
        type="button"
        className="w-full"
        data-test="sso-domain-continue-button"
        // The form's submit is removed when this appears, so keyboard focus
        // moves here rather than being lost.
        autoFocus
        disabled={isStarting}
        onClick={() =>
          signInWithSSO.mutate(
            { domain: props.domain, redirectTo: props.redirectTo },
            { onSuccess: () => recordAuthMethod('sso') },
          )
        }
      >
        {isStarting ? (
          <>
            <Spinner className="mr-2 h-4 w-4" />
            <Trans i18nKey="auth.ssoRedirecting" />
          </>
        ) : (
          <Trans i18nKey="auth.continueWithSSO" />
        )}
      </Button>

      <AuthErrorAlert error={signInWithSSO.error} />
    </div>
  );
}

function SsoDomainLookupFailedNotice() {
  return (
    <Alert variant="destructive" data-test="sso-domain-lookup-failed">
      <TriangleAlert className="h-4" />

      <AlertTitle>
        <Trans i18nKey="auth.ssoDomainLookupFailedTitle" />
      </AlertTitle>

      <AlertDescription>
        <Trans i18nKey="auth.ssoDomainLookupFailedDescription" />
      </AlertDescription>
    </Alert>
  );
}
